/**
 * Interfaces & Connectivity — network_interfaces (the canonical
 * home), user_interfaces, external_connectivity (cloud services +
 * remote access). Pulls from `formatted.interfaces_and_connectivity`.
 * Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, ObjectListField, type SectionProps, type ColumnDescriptor } from './parts';

const PATH = ['interfaces_and_connectivity'];

// Accept real booleans and legacy stringified booleans (the old text-input edit
// path could persist "true"/"True"), so a set value never renders as an em-dash.
const yesNo = (v) => {
  if (v === true || v === 'true' || v === 'True') return 'true';
  if (v === false || v === 'false' || v === 'False') return 'false';
  return '';
};
const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v);

export default function InterfacesConnectivity({ data, isEditing, onChange }: SectionProps) {
  const ic = data ?? {};
  const networkInterfaces = Array.isArray(ic.network_interfaces) ? ic.network_interfaces : [];
  const userInterfaces = ic.user_interfaces?.interfaces ?? [];
  const cloudServices = ic.external_connectivity?.cloud_services ?? [];
  const remoteAccess = ic.external_connectivity?.remote_access ?? [];

  // AUMOVIO 1d: boundary interfaces are referenced elsewhere by id (I1, I2, …),
  // so the id must be visible here to map an identifier to its description.
  const networkIfaceColumns: ColumnDescriptor[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'protocol', label: 'Protocol' },
    { key: 'external_access', label: 'External Access', type: 'boolean', render: yesNo },
    { key: 'authenticated', label: 'Authenticated', type: 'boolean', render: yesNo },
    { key: 'encrypted', label: 'Encrypted', type: 'boolean', render: yesNo },
  ];
  const userIfaceColumns: ColumnDescriptor[] = [
    { key: 'interface_id', label: 'ID' },
    { key: 'interface_type', label: 'Type' },
    { key: 'description', label: 'Description' },
    { key: 'authentication_required', label: 'Auth Required', type: 'boolean', render: yesNo },
    { key: 'authentication_methods', label: 'Auth Methods', render: joinList },
    { key: 'functions_accessible', label: 'Functions', render: joinList },
    { key: 'security_controls', label: 'Security Controls', render: joinList },
  ];
  const cloudColumns: ColumnDescriptor[] = [
    { key: 'service_provider', label: 'Provider' },
    { key: 'service_purpose', label: 'Purpose' },
    { key: 'data_exchanged', label: 'Data Exchanged', render: joinList },
    { key: 'authentication_method', label: 'Auth Method' },
    { key: 'encryption_in_transit', label: 'Encrypt in Transit', type: 'boolean', render: yesNo },
    { key: 'encryption_at_rest', label: 'Encrypt at Rest', type: 'boolean', render: yesNo },
    { key: 'data_retention_policy', label: 'Retention' },
    { key: 'compliance_requirements', label: 'Compliance', render: joinList },
  ];
  const remoteColumns: ColumnDescriptor[] = [
    { key: 'access_method', label: 'Method' },
    { key: 'purpose', label: 'Purpose' },
    { key: 'authentication_requirements', label: 'Auth Required', render: joinList },
    { key: 'authorization_levels', label: 'Authorization', render: joinList },
    { key: 'accessible_functions', label: 'Functions', render: joinList },
    { key: 'security_controls', label: 'Security Controls', render: joinList },
    { key: 'logging_monitoring', label: 'Logging', type: 'boolean', render: yesNo },
  ];

  return (
    <SectionCard title="Interfaces & Connectivity" id="interfaces_and_connectivity">
      <SubSection title="Network interfaces" count={networkInterfaces.length}>
        <ObjectListField columns={networkIfaceColumns} rows={networkInterfaces} isEditing={isEditing} onChange={onChange} path={[...PATH, 'network_interfaces']} />
      </SubSection>

      <SubSection title="User interfaces" count={userInterfaces.length}>
        <ObjectListField columns={userIfaceColumns} rows={userInterfaces} isEditing={isEditing} onChange={onChange} path={[...PATH, 'user_interfaces', 'interfaces']} />
      </SubSection>

      <SubSection title="Cloud service integrations" count={cloudServices.length}>
        <ObjectListField columns={cloudColumns} rows={cloudServices} isEditing={isEditing} onChange={onChange} path={[...PATH, 'external_connectivity', 'cloud_services']} />
      </SubSection>

      <SubSection title="Remote access" count={remoteAccess.length}>
        <ObjectListField columns={remoteColumns} rows={remoteAccess} isEditing={isEditing} onChange={onChange} path={[...PATH, 'external_connectivity', 'remote_access']} />
      </SubSection>
    </SectionCard>
  );
}
